// GoogleAdsForecastSimulator.jsx
import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import Axios from "axios";
import EChartsReact from "echarts-for-react";
import {
    Alert,
    Box,
    Button,
    Chip,
    CircularProgress,
    MenuItem,
    Slider,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import HoverPaper from "../components/shared/HoverPaper";
import InfoIcon from "../components/InfoIcon";
import { getHotelName, URLAPI } from "../configuration";
import { selectHotels } from "../features/hotels/hotelsApi";
import NoDataRow from "./NoDataRow";

const GoogleAdsForecastSimulator = ({ hotelID: hotelIDProp }) => {
    const theme = useTheme();
    const auth = useSelector((state) => state.auth);
    const { id: selectedHotelID } = useSelector((state) => state.hotelID);
    const { data: hotels = [] } = useSelector(selectHotels);
    const hotelID = hotelIDProp ?? selectedHotelID;
    const validHotel = Number.isInteger(Number(hotelID)) && Number(hotelID) > 0;
    const request = useRef(null);

    const [weeklyCost, setWeeklyCost] = useState("");
    const [currentWeeklyCost, setCurrentWeeklyCost] = useState(null);
    const [weeks, setWeeks] = useState(4);
    const [metric, setMetric] = useState("bookings");
    const [loadingCurrent, setLoadingCurrent] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [result, setResult] = useState(null);
    const [simulationInfo, setSimulationInfo] = useState(null);

    useEffect(() => {
        request.current?.abort();
        setWeeklyCost("");
        setCurrentWeeklyCost(null);
        setWeeks(4);
        setResult(null);
        setSimulationInfo(null);
        setError("");
        setLoading(false);
        setLoadingCurrent(false);
        if (!validHotel || !auth.user?.jwt) return;

        const controller = new AbortController();
        request.current = controller;
        setLoadingCurrent(true);

        Axios.post(`${URLAPI}/google_ads/simulate`, null, {
            params: {
                hotel_id: Number(hotelID),
                weekly_cost: 0,
                weeks: 4,
                allow_extrapolation: true,
            },
            headers: { Authorization: `Bearer ${auth.user.jwt}` },
            signal: controller.signal,
        })
            .then(({ data }) => {
                if (controller.signal.aborted) return;

                const prediction = data?.results ? data : data?.data;
                const current = prediction?.spend?.referenceWeeklyCost;
                const suggested = prediction?.spend?.suggestedWeeklyCost;

                if (!Number.isFinite(current) || !prediction?.curve?.length)
                    throw new Error(
                        "Invalid forecast response. Update the simulation endpoint first.",
                    );

                setCurrentWeeklyCost(current);
                setWeeklyCost(
                    (prediction.mode === "cold_start" &&
                    Number.isFinite(suggested)
                        ? suggested
                        : current
                    ).toFixed(2),
                );
                setSimulationInfo(prediction);
                setResult(prediction);
            })
            .catch((requestError) => {
                if (!controller.signal.aborted)
                    setError(
                        requestError.response?.data?.error ??
                            requestError.response?.data?.data?.error ??
                            requestError.message,
                    );
            })
            .finally(() => {
                if (!controller.signal.aborted) setLoadingCurrent(false);
            });

        return () => request.current?.abort();
    }, [auth.user?.jwt, hotelID, validHotel]);

    const validCost =
        weeklyCost !== "" &&
        Number.isFinite(Number(weeklyCost)) &&
        Number(weeklyCost) >= 0;
    const mode = result?.mode ?? simulationInfo?.mode;
    const similarHotels =
        result?.similarHotels ?? simulationInfo?.similarHotels ?? [];
    const currency =
        result?.spend?.currency || simulationInfo?.spend?.currency || "EUR";
    const revenueCurrency =
        result?.revenueCurrency || simulationInfo?.revenueCurrency || null;
    const recommendedMaxWeeklyCost =
        result?.spend?.recommendedMaxWeeklyCost ??
        simulationInfo?.spend?.recommendedMaxWeeklyCost;
    const curve = result?.curve ?? [];
    const forecast = result?.results?.[metric];
    const selected = validCost
        ? curve.find((point) => point.weeklyCost === Number(weeklyCost))
        : null;

    const money = (value, code = currency) =>
        value == null || !code
            ? "—"
            : Number(value).toLocaleString("en-GB", {
                  style: "currency",
                  currency: code,
                  maximumFractionDigits: 2,
              });
    const number = (value) =>
        value == null
            ? "—"
            : Number(value).toLocaleString("en-GB", {
                  maximumFractionDigits: 1,
              });
    const change = (value, formatter) => {
        if (value == null) return "—";
        const formatted = formatter(Math.abs(value));
        return formatted === formatter(0)
            ? formatted
            : `${value > 0 ? "+" : "−"}${formatted}`;
    };
    const format =
        metric === "bookings"
            ? number
            : (value) => money(value, revenueCurrency);

    const marginalCurve = curve.slice(1).flatMap((point, i) => {
        const previous = curve[i];
        const weeklyIncrease = point.weeklyCost - previous.weeklyCost;

        // Ignore near-duplicate spend values.
        if (weeklyIncrease <= 0.000001 || currency !== revenueCurrency)
            return [];

        return [
            {
                from: previous.weeklyCost,
                to: point.weeklyCost,
                weeklyCost: (previous.weeklyCost + point.weeklyCost) / 2,
                marginalReturn:
                    (point.revenue - previous.revenue) /
                    (weeklyIncrease * result.horizonWeeks),
            },
        ];
    });
    let weeklyCost15 = null;

    for (let i = 1; i < marginalCurve.length; i++) {
        const previous = marginalCurve[i - 1];
        const current = marginalCurve[i];

        if (
            previous.marginalReturn >= 15 &&
            current.marginalReturn <= 15 &&
            previous.marginalReturn > current.marginalReturn
        ) {
            weeklyCost15 =
                previous.weeklyCost +
                ((current.weeklyCost - previous.weeklyCost) *
                    (previous.marginalReturn - 15)) /
                    (previous.marginalReturn - current.marginalReturn);
            break;
        }
    }

    const handlePredict = async () => {
        if (
            !validHotel ||
            !validCost ||
            !auth.user?.jwt ||
            loading ||
            loadingCurrent
        )
            return;

        request.current?.abort();
        const controller = new AbortController();
        request.current = controller;
        setLoading(true);
        setError("");

        try {
            const response = await Axios.post(
                `${URLAPI}/google_ads/simulate`,
                null,
                {
                    params: {
                        hotel_id: Number(hotelID),
                        weekly_cost: Number(weeklyCost),
                        weeks,
                        allow_extrapolation: true,
                    },
                    headers: { Authorization: `Bearer ${auth.user.jwt}` },
                    signal: controller.signal,
                },
            );
            if (controller.signal.aborted) return;

            const prediction = response.data?.results
                ? response.data
                : response.data?.data;
            if (!prediction?.results || !prediction?.curve?.length)
                throw new Error("Invalid forecast response");

            setResult(prediction);
            setSimulationInfo(prediction);
            setCurrentWeeklyCost(prediction.spend.referenceWeeklyCost);
        } catch (requestError) {
            if (!controller.signal.aborted)
                setError(
                    requestError.response?.data?.error ??
                        requestError.response?.data?.data?.error ??
                        requestError.message,
                );
        } finally {
            if (!controller.signal.aborted) setLoading(false);
        }
    };

    return (
        <Box sx={{ mt: 3 }}>
            <InfoIcon
                title="Google Ads forecast"
                description="Compare no new spend, current spend and a selected weekly spend. Current spend is the average achieved spend over the last four complete weeks available to the model. No new spend retains the residual effect of previous campaigns. All outcomes are forecasts for the selected period."
            />

            <Box component={HoverPaper} sx={{ p: { xs: 2, sm: 3 } }}>
                {!validHotel ? (
                    <Table>
                        <NoDataRow text="Select a hotel." />
                    </Table>
                ) : (
                    <>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            {getHotelName(hotelID, hotels, false) ??
                                `Hotel ${hotelID}`}
                        </Typography>

                        <Stack
                            direction="row"
                            spacing={2}
                            useFlexGap
                            flexWrap="wrap"
                            alignItems="center"
                        >
                            <TextField
                                size="small"
                                label="Weekly spend to test"
                                type="number"
                                value={weeklyCost}
                                onChange={({ target }) =>
                                    setWeeklyCost(target.value)
                                }
                                onKeyDown={(event) =>
                                    event.key === "Enter" && handlePredict()
                                }
                                inputProps={{ min: 0, step: 0.01 }}
                                disabled={loading || loadingCurrent}
                                sx={{ width: 200 }}
                            />

                            <Button
                                variant="outlined"
                                disabled={
                                    currentWeeklyCost == null ||
                                    loading ||
                                    loadingCurrent
                                }
                                onClick={() =>
                                    setWeeklyCost(currentWeeklyCost.toFixed(2))
                                }
                            >
                                {loadingCurrent ? (
                                    <CircularProgress size={20} />
                                ) : (
                                    `Current: ${money(currentWeeklyCost)}`
                                )}
                            </Button>

                            <TextField
                                select
                                size="small"
                                label="Period"
                                value={weeks}
                                sx={{ width: 125 }}
                                disabled={loading || loadingCurrent}
                                onChange={({ target }) => {
                                    setWeeks(Number(target.value));
                                    setResult(null);
                                }}
                            >
                                {[1, 2, 4].map((value) => (
                                    <MenuItem key={value} value={value}>
                                        {value} week{value === 1 ? "" : "s"}
                                    </MenuItem>
                                ))}
                            </TextField>

                            <TextField
                                select
                                size="small"
                                label="Metric"
                                value={metric}
                                sx={{ width: 135 }}
                                onChange={({ target }) =>
                                    setMetric(target.value)
                                }
                            >
                                <MenuItem value="bookings">Bookings</MenuItem>
                                <MenuItem value="revenue">Revenue</MenuItem>
                            </TextField>

                            <Button
                                variant="contained"
                                onClick={handlePredict}
                                disabled={
                                    !validCost ||
                                    loading ||
                                    loadingCurrent ||
                                    !auth.user?.jwt
                                }
                            >
                                {loading ? (
                                    <CircularProgress
                                        size={22}
                                        color="inherit"
                                    />
                                ) : (
                                    "Calculate"
                                )}
                            </Button>
                        </Stack>

                        {validCost &&
                        Number(weeklyCost) > recommendedMaxWeeklyCost ? (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                The recommended simulation limit is{" "}
                                {money(recommendedMaxWeeklyCost)} / week
                                {currentWeeklyCost > 0 &&
                                    ` (${number(recommendedMaxWeeklyCost / currentWeeklyCost)}× current spend)`}
                                . You can calculate higher amounts, but these
                                estimates extrapolate far beyond historical
                                spend.
                            </Alert>
                        ) : selected &&
                          !selected.withinHistoricalRange &&
                          selected.weeklyCost > 0 ? (
                            <Alert severity="warning" sx={{ mt: 2 }}>
                                The selected spend is outside the historical
                                range.
                            </Alert>
                        ) : null}

                        {error && (
                            <Alert severity="error" sx={{ mt: 2 }}>
                                {error}
                            </Alert>
                        )}

                        {mode === "cold_start" && (
                            <Alert severity="info" sx={{ mt: 2 }}>
                                The baseline uses this hotel's reservations. The
                                Ads scenario uses similar hotels:{" "}
                                {similarHotels
                                    .map(
                                        ({ hotelID }) =>
                                            getHotelName(
                                                hotelID,
                                                hotels,
                                                false,
                                            ) ?? `Hotel ${hotelID}`,
                                    )
                                    .join(", ")}
                                .
                            </Alert>
                        )}

                        {!result && !loadingCurrent && !error && (
                            <Typography sx={{ mt: 2 }} color="text.secondary">
                                Click Calculate to load this period.
                            </Typography>
                        )}

                        {result && forecast && curve.length > 0 && (
                            <Box sx={{ mt: 3 }}>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    Forecast: {result.forecastStart} —{" "}
                                    {result.forecastEnd}. Totals over{" "}
                                    {result.horizonWeeks} week
                                    {result.horizonWeeks === 1 ? "" : "s"}.
                                </Typography>

                                <Box sx={{ px: 1, mt: 2 }}>
                                    <Slider
                                        aria-label="Weekly spend"
                                        min={0}
                                        max={curve[curve.length - 1].weeklyCost}
                                        step={null}
                                        marks={curve.map((point) => ({
                                            value: point.weeklyCost,
                                        }))}
                                        value={Math.min(
                                            curve[curve.length - 1].weeklyCost,
                                            Math.max(
                                                0,
                                                Number(weeklyCost) || 0,
                                            ),
                                        )}
                                        onChange={(_, value) =>
                                            setWeeklyCost(String(value))
                                        }
                                        valueLabelDisplay="auto"
                                        valueLabelFormat={money}
                                        disabled={loading}
                                    />
                                    <Stack
                                        direction="row"
                                        justifyContent="space-between"
                                    >
                                        <Typography variant="caption">
                                            {money(0)} / week
                                        </Typography>
                                        <Typography variant="caption">
                                            {money(
                                                curve[curve.length - 1]
                                                    .weeklyCost,
                                            )}{" "}
                                            / week
                                        </Typography>
                                    </Stack>
                                </Box>

                                {!selected && (
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        Click Calculate to get the exact
                                        forecast for this spend.
                                    </Alert>
                                )}

                                <Stack
                                    direction={{ xs: "column", sm: "row" }}
                                    spacing={3}
                                    sx={{ my: 3 }}
                                    aria-live="polite"
                                >
                                    {[
                                        [
                                            "No new spend",
                                            format(forecast.zeroTotal),
                                        ],
                                        [
                                            "Estimated lift vs 0",
                                            selected
                                                ? change(
                                                      selected[metric] -
                                                          forecast.zeroTotal,
                                                      format,
                                                  )
                                                : "—",
                                        ],
                                        [
                                            "Selected total forecast",
                                            format(selected?.[metric]),
                                        ],
                                    ].map(([label, value]) => (
                                        <Box key={label} sx={{ flex: 1 }}>
                                            <Typography
                                                variant="body2"
                                                color="text.secondary"
                                            >
                                                {label}
                                            </Typography>
                                            <Typography variant="h5">
                                                {value}
                                            </Typography>
                                        </Box>
                                    ))}
                                </Stack>

                                {selected && (
                                    <Typography
                                        variant="body2"
                                        sx={{ mb: 2 }}
                                        aria-live="polite"
                                    >
                                        Vs current:{" "}
                                        <strong>
                                            {change(
                                                selected[metric] -
                                                    forecast.referenceTotal,
                                                format,
                                            )}
                                        </strong>{" "}
                                        {metric} ·{" "}
                                        {change(
                                            (selected.weeklyCost -
                                                result.spend
                                                    .referenceWeeklyCost) *
                                                result.horizonWeeks,
                                            money,
                                        )}{" "}
                                        spend over the period.
                                    </Typography>
                                )}

                                <EChartsReact
                                    notMerge
                                    style={{ height: 330 }}
                                    option={{
                                        animation: false,
                                        textStyle: {
                                            color: theme.palette.text.primary,
                                        },
                                        legend: {
                                            data: [
                                                "No new spend",
                                                "Total forecast",
                                            ],
                                            selectedMode: false,
                                            textStyle: {
                                                color: theme.palette.text
                                                    .primary,
                                            },
                                        },
                                        grid: {
                                            left: 12,
                                            right: 20,
                                            top: 65,
                                            bottom: 55,
                                            containLabel: true,
                                        },
                                        xAxis: {
                                            type: "value",
                                            min: 0,
                                            max: curve[curve.length - 1]
                                                .weeklyCost,
                                            splitNumber: 4,
                                            name: `Weekly spend (${currency})`,
                                            nameLocation: "middle",
                                            nameGap: 32,
                                            axisLabel: { formatter: number },
                                        },
                                        yAxis: {
                                            type: "value",
                                            min: ({ min, max }) =>
                                                Math.max(
                                                    0,
                                                    Math.floor(
                                                        min -
                                                            Math.max(
                                                                (max - min) *
                                                                    0.1,
                                                                Math.abs(min) *
                                                                    0.01,
                                                                1,
                                                            ),
                                                    ),
                                                ),
                                            max: ({ min, max }) =>
                                                Math.ceil(
                                                    max +
                                                        Math.max(
                                                            (max - min) * 0.1,
                                                            Math.abs(max) *
                                                                0.01,
                                                            1,
                                                        ),
                                                ),
                                            name:
                                                metric === "bookings"
                                                    ? "Bookings"
                                                    : `Revenue (${revenueCurrency || "unknown"})`,
                                            axisLabel: {
                                                formatter: (value) =>
                                                    Number(
                                                        value,
                                                    ).toLocaleString("en-GB", {
                                                        notation: "compact",
                                                    }),
                                            },
                                        },
                                        tooltip: {
                                            trigger: "axis",
                                            confine: true,
                                            formatter: (params) => {
                                                const point =
                                                    curve[params[0]?.dataIndex];
                                                if (!point) return "";

                                                const marginal =
                                                    marginalCurve.find(
                                                        (item) =>
                                                            item.to >
                                                            point.weeklyCost,
                                                    );

                                                return `
        ${money(point.weeklyCost)} / week
        <br/>No new spend: ${format(forecast.zeroTotal)}
        <br/>Estimated lift: ${change(point[metric] - forecast.zeroTotal, format)}
        <br/>Total forecast: ${format(point[metric])}
        ${
            marginal
                ? `<br/>Marginal revenue/spend (${money(marginal.from)} → ${money(marginal.to)} / week): ${number(marginal.marginalReturn)}:1`
                : ""
        }
        ${
            point.withinHistoricalRange
                ? ""
                : "<br/>Outside historical spend range"
        }
    `;
                                            },
                                        },
                                        series: [
                                            {
                                                name: "No new spend",
                                                type: "line",
                                                showSymbol: false,
                                                data: curve.map((point) => [
                                                    point.weeklyCost,
                                                    forecast.zeroTotal,
                                                ]),
                                                itemStyle: {
                                                    color: theme.palette.text
                                                        .secondary,
                                                },
                                                lineStyle: {
                                                    type: "dashed",
                                                    width: 1,
                                                },
                                                areaStyle: { opacity: 0.08 },
                                                markLine: {
                                                    silent: true,
                                                    symbol: "none",
                                                    lineStyle: {
                                                        color: theme.palette
                                                            .grey[500],
                                                        type: "dashed",
                                                        width: 1.5,
                                                    },
                                                    label: {
                                                        formatter:
                                                            "Marginal 15:1",
                                                        position:
                                                            "insideEndTop",
                                                        color: theme.palette
                                                            .grey[500],
                                                    },
                                                    data:
                                                        weeklyCost15 == null
                                                            ? []
                                                            : [
                                                                  {
                                                                      xAxis: weeklyCost15,
                                                                  },
                                                              ],
                                                },
                                            },
                                            {
                                                name: "Estimated lift",
                                                type: "line",
                                                showSymbol: false,
                                                data: curve.map((point) => [
                                                    point.weeklyCost,
                                                    point[metric],
                                                ]),
                                                itemStyle: {
                                                    color: theme.palette.primary
                                                        .main,
                                                },
                                                lineStyle: { opacity: 0 },
                                                areaStyle: {
                                                    origin: forecast.zeroTotal,
                                                    opacity: 0.15,
                                                },
                                            },
                                            ...[false, true].map(
                                                (extrapolated) => ({
                                                    name: extrapolated
                                                        ? "Extrapolation"
                                                        : "Total forecast",
                                                    type: "line",
                                                    showSymbol: false,
                                                    data: curve.map((point) => [
                                                        point.weeklyCost,
                                                        (
                                                            extrapolated
                                                                ? point.weeklyCost <=
                                                                      result
                                                                          .spend
                                                                          .historicalRange[0] ||
                                                                  point.weeklyCost >=
                                                                      result
                                                                          .spend
                                                                          .historicalRange[1]
                                                                : point.withinHistoricalRange
                                                        )
                                                            ? point[metric]
                                                            : null,
                                                    ]),
                                                    itemStyle: {
                                                        color: theme.palette
                                                            .primary.main,
                                                    },
                                                    lineStyle: {
                                                        type: extrapolated
                                                            ? "dashed"
                                                            : "solid",
                                                        width: 2,
                                                    },
                                                }),
                                            ),
                                            {
                                                name: "Current",
                                                type: "scatter",
                                                symbol: "circle",
                                                symbolSize: 11,
                                                data: [
                                                    [
                                                        result.spend
                                                            .referenceWeeklyCost,
                                                        forecast.referenceTotal,
                                                    ],
                                                ],
                                                itemStyle: {
                                                    color: theme.palette.text
                                                        .primary,
                                                },
                                                tooltip: { show: false },
                                            },
                                            {
                                                name: "Selected",
                                                type: "scatter",
                                                symbol: "diamond",
                                                symbolSize: 17,
                                                data: selected
                                                    ? [
                                                          [
                                                              selected.weeklyCost,
                                                              selected[metric],
                                                          ],
                                                      ]
                                                    : [],
                                                itemStyle: {
                                                    color: theme.palette.primary
                                                        .main,
                                                },
                                                tooltip: { show: false },
                                            },
                                        ],
                                    }}
                                />

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                >
                                    Circle: current spend. Diamond: selected
                                    spend. Shaded area: estimated lift. Dashed
                                    curve: outside historical spend range.
                                </Typography>

                                <TableContainer sx={{ mt: 2 }}>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                {[
                                                    "Scenario",
                                                    "Spend / week",
                                                    "Spend / period",
                                                    metric === "bookings"
                                                        ? "Bookings / period"
                                                        : "Revenue / period",
                                                    "Lift vs 0",
                                                    "Change vs current",
                                                ].map((label, index) => (
                                                    <TableCell
                                                        key={label}
                                                        align={
                                                            index
                                                                ? "right"
                                                                : "left"
                                                        }
                                                    >
                                                        {label}
                                                    </TableCell>
                                                ))}
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {[
                                                [
                                                    "No new spend",
                                                    0,
                                                    forecast.zeroTotal,
                                                ],
                                                [
                                                    "Current",
                                                    result.spend
                                                        .referenceWeeklyCost,
                                                    forecast.referenceTotal,
                                                ],
                                                [
                                                    "Selected",
                                                    validCost
                                                        ? Number(weeklyCost)
                                                        : null,
                                                    selected?.[metric],
                                                ],
                                            ].map(([label, cost, total]) => (
                                                <TableRow
                                                    key={label}
                                                    sx={{
                                                        bgcolor:
                                                            label === "Selected"
                                                                ? "action.selected"
                                                                : undefined,
                                                    }}
                                                >
                                                    <TableCell>
                                                        {label}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {money(cost)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {money(
                                                            cost == null
                                                                ? null
                                                                : cost *
                                                                      result.horizonWeeks,
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {format(total)}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {change(
                                                            total == null
                                                                ? null
                                                                : total -
                                                                      forecast.zeroTotal,
                                                            format,
                                                        )}
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        {label === "Selected" &&
                                                        total != null
                                                            ? change(
                                                                  total -
                                                                      forecast.referenceTotal,
                                                                  format,
                                                              )
                                                            : "—"}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>

                                {/* <Stack
                                    direction="row"
                                    spacing={1}
                                    useFlexGap
                                    flexWrap="wrap"
                                    sx={{ mt: 2 }}
                                >
                                    {["bookings", "revenue"].map((key) => (
                                        <Chip
                                            key={key}
                                            size="small"
                                            label={`${key}: ${result.results[key].confidence} confidence`}
                                        />
                                    ))}
                                </Stack> */}

                                <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    display="block"
                                    sx={{ mt: 1 }}
                                >
                                    {mode === "cold_start"
                                        ? "Comparable hotels' weekly range"
                                        : "Historical weekly range"}
                                    : {money(result.spend.historicalRange[0])} —{" "}
                                    {money(result.spend.historicalRange[1])}.{" "}
                                    Observational estimate, not a guaranteed
                                    result.
                                </Typography>

                                {!result.spend.zeroWithinHistoricalRange && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        The zero-spend base is outside the
                                        historical spend range; lift estimates
                                        also depend on this extrapolation.
                                    </Alert>
                                )}

                                {result.dataStaleDays > 14 && (
                                    <Alert severity="warning" sx={{ mt: 2 }}>
                                        The model is {result.dataStaleDays} days
                                        behind today. Retrain before treating
                                        this as a current forecast.
                                    </Alert>
                                )}
                            </Box>
                        )}
                    </>
                )}
            </Box>
        </Box>
    );
};

export default GoogleAdsForecastSimulator;
